import React, { useEffect, useRef, useState } from "react";
import { UploadCloud, Loader2, ImageOff, Replace } from "lucide-react";
import { toast } from "sonner";
import { api, imageUrl } from "../lib/api";

/**
 * Phase 25 — Admin product image drop-zone.
 *
 * Full-featured widget:
 *   • Drag-and-drop or click to open the OS file picker
 *   • Live preview with a "Reemplazar" button
 *   • Displays file size + MIME type before upload starts
 *   • Progress bar during upload (via axios `onUploadProgress`)
 *   • Client-side validation mirrors the backend: PNG/JPEG/WebP, max 8 MB.
 *     Any rejection surfaces a Spanish toast — the backend enforces the
 *     same rules server-side so bypassing the client still fails cleanly.
 *
 * Props:
 *   productId       — the product uuid (required to build the upload URL).
 *   currentImageUrl — the initial preview (server relative path).
 *   onUploaded      — (data) => void; called with `{image_url, v}` after
 *                     a successful upload. Parent should update its form
 *                     state and pass a cache-busted URL to the next render.
 *   disabled        — disables all interactions (e.g. product not saved yet).
 */
const MAX_BYTES = 8 * 1024 * 1024;
const OK_MIMES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];

function humanSize(bytes) {
  if (!Number.isFinite(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ImageDropZone({
  productId,
  currentImageUrl,
  onUploaded,
  disabled = false,
}) {
  const [dragOver, setDragOver] = useState(false);
  const [selected, setSelected] = useState(null); // File
  const [preview, setPreview] = useState(currentImageUrl || null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  // Phase 29 bug fix — re-sync `preview` when the parent finishes loading the
  // product asynchronously. Without this, the widget mounts with an empty
  // `currentImageUrl`, the product then hydrates the form, but the preview
  // stays `null` → placeholder shown on every F5/reload even though the DB
  // has a valid image_url. Guard against clobbering an active user upload:
  // if the current preview already shares the same canonical path as the
  // incoming prop (i.e. it carries a `?v=…` cache-bust of the same file),
  // keep the cache-busted URL so we don't lose the freshly-uploaded pixels.
  useEffect(() => {
    setPreview((prev) => {
      if (!currentImageUrl) return prev || null;
      if (prev && prev.split("?")[0] === currentImageUrl) return prev;
      return currentImageUrl;
    });
  }, [currentImageUrl]);

  const openPicker = () => {
    if (disabled || busy) return;
    inputRef.current?.click();
  };

  const validate = (file) => {
    if (!file) return "Archivo no válido.";
    if (!OK_MIMES.includes(file.type)) return "Formato no soportado. Usa PNG, JPEG o WebP.";
    if (file.size > MAX_BYTES) return "Imagen demasiado grande. Máximo 8 MB.";
    return null;
  };

  const handleFile = async (file) => {
    const err = validate(file);
    if (err) {
      toast.error(err);
      return;
    }
    setSelected(file);
    // Local preview immediately
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload
    setBusy(true);
    setProgress(0);
    const t = toast.loading("Subiendo imagen…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/admin/products/${productId}/image/upload`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
          },
        },
      );
      // Backend upload/regen/AI endpoints ya devuelven image_url con ?v=<ts>.
      // Antes concatenábamos un segundo Date.now() aquí produciendo URLs con
      // doble query-string (`.png?v=A?v=B`) que se persistían sucias en DB.
      // Usamos la URL tal como llega — es única (timestamp por segundo) y ya
      // rompe el caché del CDN por sí sola.
      const cacheBust = data.image_url;
      setPreview(cacheBust);
      onUploaded?.({ ...data, image_url: cacheBust });
      toast.success("Imagen subida correctamente", { id: t });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        typeof detail === "string" && detail
          ? detail
          : "No se pudo subir la imagen",
        { id: t },
      );
      // Roll back the optimistic preview.
      setPreview(currentImageUrl || null);
      setSelected(null);
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled || busy) return;
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    if (!disabled && !busy) setDragOver(true);
  };

  const onDragLeave = () => setDragOver(false);

  const onInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset so the same file can be re-selected consecutively.
    e.target.value = "";
  };

  return (
    <div className="space-y-2" data-testid="admin-image-drop-zone">
      {/* Drop area */}
      <div
        onClick={openPicker}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        role="button"
        tabIndex={0}
        aria-disabled={disabled}
        data-testid="admin-image-dropzone-target"
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openPicker();
          }
        }}
        className={`relative rounded-2xl border-2 border-dashed p-4 transition-colors cursor-pointer select-none ${
          disabled
            ? "border-neutral-200 bg-neutral-50 opacity-60 cursor-not-allowed"
            : dragOver
            ? "border-[#00953B] bg-[#F1FAF3]"
            : "border-neutral-300 hover:border-[#00953B] hover:bg-neutral-50"
        }`}
      >
        <div className="flex items-center gap-4">
          {/* Preview — the wrapper always carries `admin-image-preview`
              so tests can locate the slot even before an image loads. */}
          <div
            data-testid="admin-image-preview"
            className="h-28 w-28 shrink-0 rounded-xl bg-white border border-neutral-200 grid place-items-center overflow-hidden"
          >
            {preview ? (
              <img
                src={imageUrl(preview)}
                alt="Vista previa"
                data-testid="admin-image-preview-img"
                className="h-full w-full object-contain"
              />
            ) : (
              <ImageOff className="h-6 w-6 text-neutral-300" />
            )}
          </div>
          {/* Body */}
          <div className="flex-1 min-w-0">
            {dragOver ? (
              <p className="font-semibold text-[#00753a]" data-testid="admin-image-dropzone-hint">
                Suelta la imagen aquí
              </p>
            ) : (
              <>
                <p className="text-sm font-semibold text-neutral-800 flex items-center gap-1.5">
                  <UploadCloud className="h-4 w-4 text-[#00953B]" />
                  {preview ? "Cambia la imagen" : "Arrastra una imagen o pulsa para subir"}
                </p>
                <p className="text-xs text-neutral-500 mt-1">
                  Formatos aceptados: PNG · JPEG · WebP · máx. 8 MB
                </p>
                {selected && !busy && (
                  <p className="text-xs text-neutral-500 mt-0.5" data-testid="admin-image-file-info">
                    {selected.name} · {humanSize(selected.size)} · {selected.type.split("/")[1].toUpperCase()}
                  </p>
                )}
              </>
            )}
            {busy && (
              <div className="mt-2 space-y-1" data-testid="admin-image-progress">
                <div className="flex items-center gap-2 text-xs text-neutral-600">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Subiendo {progress}%
                </div>
                <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#00953B] transition-[width]"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
          {/* Replace button — always rendered so the testid is stable.
              Disabled when no preview yet or an upload is in progress. */}
          {!busy && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openPicker();
              }}
              disabled={disabled || !preview}
              data-testid="admin-image-replace-btn"
              className={`text-xs flex items-center gap-1 px-3 h-9 rounded-full border bg-white ${
                preview
                  ? "border-neutral-200 hover:border-[#00953B] hover:text-[#00753a]"
                  : "border-neutral-100 text-neutral-300 cursor-not-allowed"
              } disabled:opacity-50`}
            >
              <Replace className="h-3 w-3" />
              Reemplazar
            </button>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={onInputChange}
          data-testid="admin-image-input"
          className="sr-only"
          tabIndex={-1}
        />
      </div>
    </div>
  );
}
